import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  Input,
  OnDestroy,
  signal,
} from '@angular/core';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { MzicSvgComponent } from '@mzic/mzic-components';

export type AuthenticatingStatus =
  | 'CONNECTING'
  | 'IN_PROGRESS'
  | 'COMPLETE'
  | 'ERROR';

interface DownloadingStep {
  index: number;
}

@Component({
  selector: 'lib-authenticating-overlay',
  imports: [CommonModule, TranslocoModule, MzicSvgComponent],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './authenticating-overlay.component.html',
  styleUrl: './authenticating-overlay.component.scss',
})
export class AuthenticatingOverlayComponent implements OnDestroy {
  @Input() entityName = '';
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() entityIconUrl: string | null = null;
  @Input() set status(value: AuthenticatingStatus) {
    this._status.set(value);
  }

  private readonly _status = signal<AuthenticatingStatus>('CONNECTING');

  readonly isConnecting = computed(() => this._status() === 'CONNECTING');
  readonly isDownloading = computed(() => this._status() === 'IN_PROGRESS');
  readonly isComplete = computed(() => this._status() === 'COMPLETE');
  readonly isError = computed(() => this._status() === 'ERROR');

  // Dynamic text cycling for downloading state
  private readonly downloadingSteps: DownloadingStep[] = [
    { index: 1 },
    { index: 2 },
    { index: 3 },
    { index: 4 },
    { index: 5 },
  ];

  readonly currentStepIndex = signal(0);
  readonly isTextFading = signal(false); // Controls CSS fade transition
  readonly stepDuration = 3000; // 3 seconds per step
  private readonly fadeDuration = 250; // CSS transition duration in ms
  private animationTimeoutId?: number;
  private isAnimationStopped = false;

  // Computed signal for current downloading title key
  readonly currentDownloadingTitleKey = computed(() => {
    const index = this.currentStepIndex();
    const step = this.downloadingSteps[index]?.index ?? 1;
    return `lang.downloading_step_${step}${this.integrationSuffix}`;
  });

  get integrationSuffix(): string {
    return this.integrationEnabled ? '_integration' : '';
  }

  get resolvedEntityName(): string {
    if (this.entityName) {
      return this.entityName;
    }
    if (this.integrationEnabled && this.integrationEntityName) {
      return this.integrationEntityName;
    }
    return this.entityName;
  }

  get connectingToKey(): string {
    return `lang.authenticating_connecting_to${this.integrationSuffix}`;
  }

  get downloadingSubtitleKey(): string {
    return `lang.authenticating_downloading_subtitle${this.integrationSuffix}`;
  }

  get completeSubtitleKey(): string {
    return `lang.authenticating_complete_subtitle${this.integrationSuffix}`;
  }

  get errorSubtitleKey(): string {
    return `lang.authenticating_error_subtitle${this.integrationSuffix}`;
  }

  constructor() {
    // Effect to start/stop animation based on status changes
    effect(
      () => {
        const status = this._status();

        if (status === 'IN_PROGRESS') {
          this.startDownloadingAnimation();
        } else {
          this.stopDownloadingAnimation();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.stopDownloadingAnimation();
  }

  private startDownloadingAnimation(): void {
    // Reset state
    this.isAnimationStopped = false;
    this.currentStepIndex.set(0);
    this.isTextFading.set(false);

    // Start cycling through steps
    this.scheduleNextStep();
  }

  private stopDownloadingAnimation(): void {
    this.isAnimationStopped = true;

    if (this.animationTimeoutId) {
      clearTimeout(this.animationTimeoutId);
      this.animationTimeoutId = undefined;
    }
  }

  private scheduleNextStep(): void {
    if (this.isAnimationStopped) {
      return;
    }

    this.animationTimeoutId = window.setTimeout(() => {
      if (this.isAnimationStopped) {
        return;
      }

      // Start fade out
      this.isTextFading.set(true);

      // After fade out completes, change text and fade back in
      window.setTimeout(() => {
        if (this.isAnimationStopped) {
          return;
        }

        // Move to next step (loop back to 0 if at end)
        const nextIndex =
          (this.currentStepIndex() + 1) % this.downloadingSteps.length;
        this.currentStepIndex.set(nextIndex);

        // Fade back in
        this.isTextFading.set(false);

        // Schedule next cycle
        this.scheduleNextStep();
      }, this.fadeDuration);
    }, this.stepDuration);
  }
}
